/**
 * Painel completo de gerenciamento de avaliações.
 *
 * Aberto via botão na Toolbar — fica em overlay full-screen sobre o
 * editor. Lista todos os reviews (com filtros), permite edit/approve/
 * reject/delete em massa, e dispara o gerador via modal.
 */

import { useEffect, useState } from 'react';
import { useEditor } from '../../state/store';
import {
  fetchReviews,
  updateReview,
  deleteReview,
  type Review,
} from '../../api/client';
import { ReviewGeneratorModal } from './ReviewGeneratorModal';

interface ReviewsPanelProps {
  onClose: () => void;
}

export function ReviewsPanel({ onClose }: ReviewsPanelProps) {
  const session = useEditor((s) => s.session);
  const [items, setItems] = useState<Review[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<Review | null>(null);
  const [generatorOpen, setGeneratorOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !editing && !generatorOpen) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing, generatorOpen, onClose]);

  useEffect(() => {
    if (!session) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status, session]);

  async function load() {
    if (!session) return;
    setBusy(true);
    try {
      const r = await fetchReviews(session, {
        page,
        per_page: 20,
        status: status || undefined,
      });
      setItems(r.reviews || []);
      setTotal(r.total || 0);
      setTotalPages(r.totalPages || 1);
    } catch (e) {
      console.error('Failed to load reviews', e);
    } finally {
      setBusy(false);
    }
  }

  async function setStatusFor(review: Review, next: Review['status']) {
    if (!session) return;
    await updateReview(session, review.id, { status: next });
    setItems((s) => s.map((r) => (r.id === review.id ? { ...r, status: next } : r)));
  }

  async function onDelete(review: Review) {
    if (!session) return;
    if (!confirm(`Deletar avaliação de ${review.author_name}?`)) return;
    await deleteReview(session, review.id);
    setItems((s) => s.filter((r) => r.id !== review.id));
    setTotal((t) => t - 1);
  }

  return (
    <div className="fixed inset-0 z-[150] flex flex-col bg-surface-subdued">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border bg-white px-6 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            aria-label="Fechar"
            className="flex h-8 w-8 items-center justify-center rounded-md text-text-subdued hover:bg-surface hover:text-text"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <h1 className="text-base font-semibold">Avaliações</h1>
          <span className="text-xs text-text-subdued">
            {total} total{total === 1 ? '' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="rounded-md border border-border bg-white px-2 py-1.5 text-sm"
          >
            <option value="">Todos os status</option>
            <option value="approved">Aprovados</option>
            <option value="pending">Pendentes</option>
            <option value="rejected">Rejeitados</option>
          </select>
          <button
            onClick={() => setGeneratorOpen(true)}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-text-inverse hover:opacity-90"
          >
            + Gerar avaliações
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {busy && items.length === 0 && (
          <div className="text-center text-sm text-text-subdued">Carregando…</div>
        )}
        {!busy && items.length === 0 && (
          <div className="rounded-lg border border-dashed border-border bg-white p-12 text-center">
            <p className="mb-2 text-sm font-medium">Nenhuma avaliação ainda</p>
            <p className="mb-4 text-xs text-text-subdued">
              Use o gerador pra criar uma seed inicial, ou aguarde visitantes da loja enviarem.
            </p>
            <button
              onClick={() => setGeneratorOpen(true)}
              className="rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-text-inverse hover:opacity-90"
            >
              Gerar avaliações agora
            </button>
          </div>
        )}
        {items.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-border bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-surface text-xs text-text-subdued">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Cliente</th>
                  <th className="px-3 py-2 text-left font-medium">Nota</th>
                  <th className="px-3 py-2 text-left font-medium">Texto</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-right font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-b border-border/50 last:border-0 hover:bg-surface/50">
                    <td className="px-3 py-2.5 align-top">
                      <div className="font-medium text-text">{r.author_name}</div>
                      <div className="text-[11px] text-text-subdued">
                        {new Date(r.created_at).toLocaleDateString('pt-BR')}
                        {r.verified && (
                          <span className="ml-2 rounded bg-green-100 px-1 py-0.5 text-[10px] font-medium text-green-700">
                            ✓ Verificado
                          </span>
                        )}
                        {r.generated && (
                          <span className="ml-2 rounded bg-purple-100 px-1 py-0.5 text-[10px] font-medium text-purple-700">
                            Gerado
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      <span style={{ color: '#FFA500' }}>{'★'.repeat(r.rating)}</span>
                      <span className="text-text-subdued">{'★'.repeat(5 - r.rating)}</span>
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      <p className="line-clamp-2 max-w-[400px] text-text">{r.text}</p>
                      {r.product_uuid && (
                        <p className="mt-1 text-[10px] text-text-subdued">
                          Produto: <code className="font-mono">{r.product_uuid.slice(0, 8)}…</code>
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-3 py-2.5 align-top text-right">
                      <div className="flex justify-end gap-1">
                        {r.status !== 'approved' && (
                          <button
                            onClick={() => setStatusFor(r, 'approved')}
                            title="Aprovar"
                            className="rounded p-1.5 text-green-700 hover:bg-green-50"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                              <path d="M20 6 9 17l-5-5" />
                            </svg>
                          </button>
                        )}
                        {r.status !== 'rejected' && (
                          <button
                            onClick={() => setStatusFor(r, 'rejected')}
                            title="Rejeitar"
                            className="rounded p-1.5 text-amber-700 hover:bg-amber-50"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                              <path d="M18 6 6 18M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => setEditing(r)}
                          title="Editar"
                          className="rounded p-1.5 text-text-subdued hover:bg-surface hover:text-primary"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => onDelete(r)}
                          title="Deletar"
                          className="rounded p-1.5 text-text-subdued hover:bg-red-50 hover:text-red-600"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M5 6l1 14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-14" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 border-t border-border bg-white px-6 py-3 text-sm">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page <= 1 || busy}
            className="rounded-md border border-border px-3 py-1 disabled:opacity-40"
          >
            ◀
          </button>
          <span className="text-text-subdued">
            Página <strong className="text-text">{page}</strong> de{' '}
            <strong className="text-text">{totalPages}</strong>
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages || busy}
            className="rounded-md border border-border px-3 py-1 disabled:opacity-40"
          >
            ▶
          </button>
        </div>
      )}

      {editing && (
        <EditReviewModal
          review={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setItems((s) => s.map((r) => (r.id === updated.id ? updated : r)));
            setEditing(null);
          }}
        />
      )}

      {generatorOpen && (
        <ReviewGeneratorModal
          onClose={() => setGeneratorOpen(false)}
          onGenerated={() => {
            setGeneratorOpen(false);
            setPage(1);
            void load();
          }}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: Review['status'] }) {
  const cfg = {
    approved: { label: 'Aprovado', bg: 'bg-green-100', text: 'text-green-700' },
    pending: { label: 'Pendente', bg: 'bg-amber-100', text: 'text-amber-700' },
    rejected: { label: 'Rejeitado', bg: 'bg-red-100', text: 'text-red-700' },
  }[status] || { label: status, bg: 'bg-surface', text: 'text-text' };
  return (
    <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
}

function EditReviewModal({
  review,
  onClose,
  onSaved,
}: {
  review: Review;
  onClose: () => void;
  onSaved: (r: Review) => void;
}) {
  const session = useEditor((s) => s.session);
  const [text, setText] = useState(review.text);
  const [authorName, setAuthorName] = useState(review.author_name);
  const [rating, setRating] = useState<number>(review.rating);
  const [verified, setVerified] = useState(review.verified);
  const [busy, setBusy] = useState(false);

  async function onSubmit() {
    if (!session) return;
    setBusy(true);
    try {
      const updated = await updateReview(session, review.id, {
        text,
        author_name: authorName,
        rating: rating as 1 | 2 | 3 | 4 | 5,
        verified,
      });
      onSaved(updated);
    } catch (e: any) {
      alert('Erro: ' + (e?.message || 'falha ao salvar'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">Editar avaliação</h2>
          <button onClick={onClose} className="text-text-subdued hover:text-text">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium">Nome</span>
          <input
            value={authorName}
            onChange={(e) => setAuthorName(e.target.value)}
            className="w-full rounded-md border border-border px-3 py-2 text-sm"
          />
        </label>

        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium">Nota</span>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(n)}
                className={`text-2xl ${n <= rating ? 'text-amber-500' : 'text-gray-300'}`}
              >
                ★
              </button>
            ))}
          </div>
        </label>

        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium">Texto</span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            className="w-full rounded-md border border-border px-3 py-2 text-sm"
          />
        </label>

        <label className="mb-4 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={verified}
            onChange={(e) => setVerified(e.target.checked)}
          />
          <span>Compra verificada</span>
        </label>

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-surface"
          >
            Cancelar
          </button>
          <button
            onClick={onSubmit}
            disabled={busy}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-text-inverse hover:opacity-90 disabled:opacity-50"
          >
            {busy ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}
